import { FC, useEffect, useMemo, useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetCountryListForDropdowns,
  useGetFeatureList,
} from "../../../../../api/services/client/clientApis";
import {
  FeaturesType,
  GetGenericAccountsResponse,
} from "../../../../../api/services/client/models";
import { useGetDeliveryOfficeListQuery } from "../../../../../api/services/mdmOffice/mdmOfficeApis";
import {
  useGetProgramClass,
  useGetSolutionOffering,
} from "../../../../../api/services/programTemplate/programTemplateApis";
import {
  FormController,
  MuiGrid,
  MuiMultiSelect,
  MuiSelect,
} from "../../../../../components";
import {
  LANGUAGE_CODE,
  PAGE_IDS,
  TOAST_AUTO_HIDE_DURATION,
} from "../../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../../helpers/constants/siteCoreKeys";
import {
  getCountryCodeWithoutCountryPrefix,
  isEmpty,
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../../helpers/utils";
import { useMultiSelectARIALabels } from "../../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../redux/slices/appState/appStateSlice";
import {
  flatArrayAndMapNamesToSiteCore,
  getFeaturesByMatch,
  SECTION_CONFIG,
} from "../../helper";
import {
  CreateGenericAccountFormKey,
  FeaturesCheckBoxes,
} from "../../modelsAndSchemas/Models";
import { createGenericAccounts } from "../../siteCore/SiteCoreKeys";
import { CreateGenericAccountConfigurationFormWrapper } from "./CreateGenericAccountConfigurationForm.styles";
import { CreateGenericAccountCheckbox } from "./createGenericAccountCheckbox/CreateGenericAccountCheckbox";

export const pageId = PAGE_IDS.CREATE_GENERIC_ACCOUNT;

interface CreateGenericAccountConfigurationFormProps {
  isEditGenericAccount?: boolean;
  deliveryOfficeRef: React.RefObject<{
    resetValues: (values?: string[]) => void;
  }>;
  genericAccountData?: GetGenericAccountsResponse;
}

const gridSize = { lg: 4, md: 6, xs: 12, sm: 6 };

export const CreateGenericAccountConfigurationForm: FC<
  CreateGenericAccountConfigurationFormProps
> = ({ deliveryOfficeRef, isEditGenericAccount, genericAccountData }) => {
  const {
    fetchLabel,
    commonLabels,
    fetchValidationMessage,
    fetchDropdownValues,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const {
    control,
    setError,
    clearErrors,
    formState: { errors },
    setValue,
    resetField,
    getValues,
  } = useFormContext();

  const dispatch = useDispatch();

  const {
    createGenericAccountConfigurationTitle,
    nextView,
    configurationCountry,
    deliveryOffice,
    nextProgramClass,
    createGenericAccountFeaturesTitle,
    assessmentsAndTasksList: assessmentsAndTasksListKey,
    coachMeetingList: coachMeetingListKey,
    eventsAndWebinarsList: eventsAndWebinarsListKey,
    toolsList: toolsListKey,
  } = createGenericAccounts;

  const { fields, replace } = useFieldArray({
    control,
    name: CreateGenericAccountFormKey.features,
    keyName: CreateGenericAccountFormKey.featuresUniqueId,
  });

  const [selectedCountryId, setSelectedCountryId] = useState<string>();
  const [selectedProgramClassId, setSelectedProgramClassId] =
    useState<string>();

  const { data: countryListData } = useGetCountryListForDropdowns();

  const {
    refetchWithParams: refetchDeliveryOffice,
    data: deliveryOfficeListData,
    isLoading: deliveryOfficeListPending,
    isError: isDeliveryOfficeListError,
    isSuccess: isDeliveryOfficeListSuccess,
  } = useGetDeliveryOfficeListQuery();

  const {
    data: solutionOfferings,
    isLoading: offeringPending,
    isError: isSolutionOfferingError,
  } = useGetSolutionOffering(LANGUAGE_CODE.EN_US, true);

  const {
    data: programClasses,
    isLoading: classPending,
    refetchWithParams: refetchProgramClass,
    isError: isProgramClassError,
  } = useGetProgramClass();

  const {
    refetchWithParams: refetchFeatureList,
    data: featureListData,
    isLoading: featureListPending,
    isError: isFeatureListError,
    isSuccess: isFeatureListSuccess,
  } = useGetFeatureList();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          offeringPending ||
          classPending ||
          deliveryOfficeListPending ||
          featureListPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    offeringPending,
    classPending,
    deliveryOfficeListPending,
    featureListPending,
  ]);

  useEffect(() => {
    if (
      isSolutionOfferingError ||
      isProgramClassError ||
      isDeliveryOfficeListError ||
      isFeatureListError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
          autoHideDuration: TOAST_AUTO_HIDE_DURATION,
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [
    isSolutionOfferingError,
    isProgramClassError,
    isDeliveryOfficeListError,
    isFeatureListError,
  ]);

  const getLabel = (key: string) => {
    return fetchLabel(createGenericAccountConfigurationTitle, key);
  };

  const getFeaturesLabel = (key: string) => {
    return fetchLabel(createGenericAccountFeaturesTitle, key);
  };

  const getFeaturesDropdownList = (key: string) => {
    return fetchDropdownValues(createGenericAccountFeaturesTitle, key);
  };

  const getCombinedSiteCoreDropDownList = () => {
    const toolsSiteCoreList = getFeaturesDropdownList(toolsListKey);

    const assessmentsAndTasksSiteCoreList = getFeaturesDropdownList(
      assessmentsAndTasksListKey,
    );

    const eventsAndWebinarsSiteCoreList = getFeaturesDropdownList(
      eventsAndWebinarsListKey,
    );

    const coachMeetingSiteCoreList =
      getFeaturesDropdownList(coachMeetingListKey);

    return [
      ...toolsSiteCoreList,
      ...assessmentsAndTasksSiteCoreList,
      ...eventsAndWebinarsSiteCoreList,
      ...coachMeetingSiteCoreList,
    ];
  };

  const flatArrayData = useMemo(() => {
    if (
      siteCoreLoading ||
      !isFeatureListSuccess ||
      !Array.isArray(featureListData) ||
      featureListData.length === 0
    ) {
      return [];
    }

    // build sections only when non‑empty
    const sections = SECTION_CONFIG.reduce<
      Array<{ title: string; items: FeaturesType[] }>
    >((acc, { match, headingKey }) => {
      const items = getFeaturesByMatch(featureListData, match);
      if (items.length > 0) {
        acc.push({
          title: getFeaturesLabel(headingKey),
          items,
        });
      }
      return acc;
    }, []);

    // flatten + annotate
    const flatArray = flatArrayAndMapNamesToSiteCore(
      sections,
      getCombinedSiteCoreDropDownList(),
    );

    if (
      isEditGenericAccount &&
      genericAccountData?.programClass?.id === getValues().nextProgramClass
    ) {
      const updatedFlatArray = flatArray.map((item) => {
        const itemExist = genericAccountData?.features.find(
          (el) => el.siteCoreKey === item.siteCoreKey,
        );
        return {
          ...item,
          isChecked: !!itemExist,
        };
      });
      return updatedFlatArray;
    }

    return flatArray;
  }, [
    featureListData,
    isFeatureListSuccess,
    isEditGenericAccount,
    genericAccountData,
    siteCoreLoading,
  ]);

  useEffect(() => {
    replace(flatArrayData);
  }, [flatArrayData, replace]);

  const clearDeliveryOfficeError = () => {
    if (isNotEmpty(errors)) {
      clearErrors(CreateGenericAccountFormKey.deliveryOffice);
    }
  };

  const handleItemChangedOrDropdownClosed = (
    selectedValues: Array<string | number>,
  ) => {
    if (isEmpty(selectedValues)) {
      setError(CreateGenericAccountFormKey.deliveryOffice, {
        type: "custom",
        message: fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
      });
    } else {
      clearDeliveryOfficeError();
    }
  };

  const getNextProgramClassData = (solutionOfferingsId: string) => {
    refetchProgramClass({
      languageCode: LANGUAGE_CODE.EN_US,
      solutionOfferingId: solutionOfferingsId,
    });
  };

  const handleSolutionOfferingChange = async (id: string) => {
    if (isEditGenericAccount) {
      setValue(CreateGenericAccountFormKey.nextProgramClass, "", {
        shouldValidate: true,
      });
    } else {
      resetField(CreateGenericAccountFormKey.nextProgramClass);
    }
    setSelectedProgramClassId("");
    getNextProgramClassData(id);
  };

  const getDeliveryOfficeData = (countryId: string) => {
    setSelectedCountryId(countryId);
    refetchDeliveryOffice(
      countryId
        ? getCountryCodeWithoutCountryPrefix(countryId).toUpperCase()
        : "",
    );
  };

  useEffect(() => {
    if (isEditGenericAccount && genericAccountData) {
      const { country, solutionOffering, programClass } = genericAccountData;

      if (country) {
        getDeliveryOfficeData(country?.id);
      }
      if (solutionOffering) {
        getNextProgramClassData(solutionOffering?.id);
      }

      if (programClass) {
        setSelectedProgramClassId(programClass?.id);
      }
    }
  }, [isEditGenericAccount, genericAccountData]);

  const deliveryOfficeFormValues = useWatch({
    name: CreateGenericAccountFormKey.deliveryOffice,
    control,
  });

  useEffect(() => {
    if (isEditGenericAccount) {
      deliveryOfficeRef?.current?.resetValues(deliveryOfficeFormValues);
    }
  }, [
    deliveryOfficeFormValues,
    isEditGenericAccount,
    isDeliveryOfficeListSuccess,
  ]);

  const handleCountryChange = (countryId: string) => {
    if (deliveryOfficeRef?.current) {
      deliveryOfficeRef?.current?.resetValues();
    }
    if (isEditGenericAccount) {
      setValue(CreateGenericAccountFormKey.deliveryOffice, "", {
        shouldValidate: true,
      });
    } else {
      resetField(CreateGenericAccountFormKey.deliveryOffice);
    }
    getDeliveryOfficeData(countryId);
  };

  const handleProgramClassChange = (programClassId: string) => {
    setSelectedProgramClassId(programClassId);
  };

  useEffect(() => {
    refetchFeatureList({
      countryId: selectedCountryId ?? "",
      programClassId: selectedProgramClassId ?? "",
    });
  }, [selectedCountryId, selectedProgramClassId]);

  return (
    <CreateGenericAccountConfigurationFormWrapper>
      <MuiGrid container className="main-container">
        <MuiGrid size={gridSize} className="form-field">
          <FormController
            control={control}
            name={CreateGenericAccountFormKey.nextView}
          >
            <MuiSelect
              id={`${pageId}-${CreateGenericAccountFormKey.nextView}`}
              label={getLabel(nextView)}
              isMandatory
              visibleDefaultSelectOption
              showSearch
              options={setApiDataToDropDownOption(solutionOfferings)}
              onChange={(e) =>
                handleSolutionOfferingChange(e.target.value as string)
              }
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className="form-field">
          <FormController
            control={control}
            name={CreateGenericAccountFormKey.country}
          >
            <MuiSelect
              id={`${pageId}-${CreateGenericAccountFormKey.country}`}
              label={getLabel(configurationCountry)}
              isMandatory
              visibleDefaultSelectOption
              disabled={isEditGenericAccount}
              onChange={(e) => handleCountryChange(e.target.value as string)}
              options={setApiDataToDropDownOption(countryListData, true)}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
              showSearch
              searchPlaceholder={commonLabels.search}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className="form-field">
          <FormController
            control={control}
            name={CreateGenericAccountFormKey.deliveryOffice}
            ref={deliveryOfficeRef}
          >
            <MuiMultiSelect
              id={`${pageId}-${CreateGenericAccountFormKey.deliveryOffice}`}
              options={setApiDataToDropDownOption(deliveryOfficeListData)}
              label={getLabel(deliveryOffice)}
              isMandatory
              showSelectAllOption
              showSearch
              placeholder={commonLabels.selectPlaceHolder}
              onItemsChangedOrDropdownClosed={(
                selectedValues: Array<string | number>,
              ) => handleItemChangedOrDropdownClosed(selectedValues)}
              showChip
              dialogTitle={commonLabels.selectedCountries}
              selectPlaceHolder={commonLabels.selectPlaceHolder}
              searchPlaceHolder={commonLabels.search}
              selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
              doneText={commonLabels.done}
              allARIAProps={multiSelectARIALabels}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className="form-field">
          <FormController
            control={control}
            name={CreateGenericAccountFormKey.nextProgramClass}
          >
            <MuiSelect
              id={`${pageId}-${CreateGenericAccountFormKey.nextProgramClass}`}
              label={getLabel(nextProgramClass)}
              visibleDefaultSelectOption
              options={setApiDataToDropDownOption(programClasses)}
              isMandatory
              showSearch
              placeholder={commonLabels.selectPlaceHolder}
              onChange={(e) =>
                handleProgramClassChange(e.target.value as string)
              }
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
      {isNotEmpty(featureListData) && (
        <CreateGenericAccountCheckbox
          fieldArray={fields as unknown as FeaturesCheckBoxes[]}
        />
      )}
    </CreateGenericAccountConfigurationFormWrapper>
  );
};
